import {useStore} from "hooks/useStore";
import moment from "moment";
import {useMemo} from "react";
import {
    buildPaymentTransactionLogFilterParams,
    normalizeCompanyIdForApi,
    normalizeSubCompanyIdForApi
} from "../buildPaymentTransactionLogListParams";

export const useCreditLog = (
    companyId,
    subCompanyId,
    selectedDateRange,
    isDateRangeChecked,
    isPeriodChecked,
    selectedPeriod,
    selectedTransactionType,
    selectedSubType,
    selectedHasWalletTransaction,
    workType,
    searchText,
    selectedInvoiceStatus,
    activeTab,
    setLoadingExcelData,
    setLoadingExcelDataV2,
    selectedProviderCompany,
    selectedCreditMode
) => {
    const stores = useStore();
    const resolvedCompanyId = normalizeCompanyIdForApi(companyId);
    const resolvedSubCompanyId = normalizeSubCompanyIdForApi(subCompanyId);

    const getExcelTemplateData = async (isV2 = false) => {
        const setLoading = isV2 ? setLoadingExcelDataV2 : setLoadingExcelData;
        setLoading(true);

        try {
            const sharedFilters = buildPaymentTransactionLogFilterParams({
                companyId: resolvedCompanyId,
                selectedDateRange,
                isDateRangeChecked,
                isPeriodChecked,
                selectedPeriod,
                selectedTransactionType,
                selectedSubType,
                selectedHasWalletTransaction,
                workType,
                searchText,
                selectedInvoiceStatus,
                selectedProviderCompany,
                selectedCreditMode,
                activeTab
            });
            const response = await stores.companyStore.getSubCompanyPaymentTransactionLogList({
                ...(resolvedSubCompanyId ? {subCompanyId: resolvedSubCompanyId} : {}),
                skip: 0,
                limit: -1,
                ...sharedFilters,
                ...(isV2 ? {excelV2: true} : {excel: true})
            });
            // With responseType "blob", an error response's body (e.g. a JSON error message)
            // still arrives as a Blob — only build a download link on an actual 200, otherwise
            // we'd silently hand the user a ".csv" file containing an error message.
            if (response?.status !== 200) {
                return {href: null, download: null};
            }
            const blob = response?.data;
            const href = blob ? window.URL.createObjectURL(blob) : null;
            const download = excelFileName;
            return {href, download};
        } catch (error) {
            console.log("error", error);
            return {href: null, download: null};
        } finally {
            setLoading(false);
        }
    };

    const excelFileName = useMemo(() => {
        let name = "";

        if (subCompanyId && subCompanyId !== "all") {
            name +=
                (stores.companyStore.subCompanyList.find(
                    subCompany => subCompany._id === subCompanyId
                )?.name || "") + " ";
        } else if (subCompanyId === "all") {
            name += "Tümü ";
        }

        if (selectedDateRange?.length === 2) {
            name += ` ${moment(selectedDateRange[0]).format("DD.MM.YYYY")} - ${moment(
                selectedDateRange[1]
            ).format("DD.MM.YYYY")} `;
        }

        if (selectedTransactionType?.length > 0) {
            name += ` ${selectedTransactionType.join(",")} `;
        }

        if (selectedSubType?.length > 0) {
            name += ` ${selectedSubType.join(",")} `;
        }

        if (selectedHasWalletTransaction !== "all") {
            name += ` ${
                selectedHasWalletTransaction === "true" ? "Cüzdanda Var" : "Cüzdanda Yok"
            } `;
        }

        name = name + "Muhasebe Kredi Loglari.csv";

        return name;
    }, [
        subCompanyId,
        selectedTransactionType,
        selectedDateRange,
        selectedSubType,
        selectedHasWalletTransaction,
        stores.companyStore.subCompanyList
    ]);

    const handleDownloadExcel = () => {
        getExcelTemplateData(false).then(result => {
            if (result?.href && result?.download) {
                const link = document.createElement("a");
                link.href = result.href;
                link.download = result.download;
                link.click();
                window.URL.revokeObjectURL(result.href);
            }
        });
    };

    const handleDownloadExcelV2 = () => {
        getExcelTemplateData(true).then(result => {
            if (result?.href && result?.download) {
                const link = document.createElement("a");
                link.href = result.href;
                link.download = result.download;
                link.click();
                window.URL.revokeObjectURL(result.href);
            }
        });
    };

    return {
        handleDownloadExcel,
        handleDownloadExcelV2
    };
};
